import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  Sparkles, LayoutDashboard, Users, Tag, BookMarked, FileText, ReceiptEuro, Receipt, Calendar, Sliders, Bell, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const TOUR_STEPS = [
  { title: "Benvenuto in K Gestionale!", icon: Sparkles,
    body: "Ti mostriamo in un minuto dove trovare tutto. Puoi andare avanti, tornare indietro o saltare la guida quando vuoi." },
  { target: "sidebar-nav-dashboard", title: "Panoramica", icon: LayoutDashboard,
    body: "Il riepilogo della tua attività: preventivi accettati, incassi, scadenze e utile. Qui trovi anche la lista «Primi passi» da spuntare." },
  { target: "sidebar-nav-contacts", title: "Contatti & Clienti", icon: Users,
    body: "Clicca «Nuovo Contatto», scegli se è un Cliente o un Fornitore e inserisci nome, telefono, email e P.IVA. I contatti sono in ordine alfabetico; dalla scheda apri anche l'archivio documenti protetto da PIN." },
  { target: "sidebar-nav-price-list", title: "Listino Prezzi", icon: Tag,
    body: "Le lavorazioni che fai più spesso, con prezzo, unità di misura e IVA. Le richiami nei preventivi con un clic." },
  { target: "sidebar-nav-preziario", title: "Preziari", icon: BookMarked,
    body: "Crea un preziario e importa il file Excel, PDF o CSV (regionale o tuo). Puoi applicare un ricarico % su tutte le voci." },
  { target: "sidebar-nav-quotes", title: "Preventivi", icon: FileText,
    body: "«Nuovo Preventivo»: scegli il cliente, aggiungi le voci dal listino o dal preziario e l'IVA si calcola da sola. Scarica il PDF o invialo su WhatsApp. Con «Importa da file» trasformi un vecchio preventivo (foto o PDF) in pochi secondi." },
  { target: "sidebar-nav-invoices", title: "Fatture", icon: ReceiptEuro,
    body: "Quando il cliente accetta, converti il preventivo in fattura, dividi in rate e segna gli incassi man mano." },
  { target: "sidebar-nav-expenses", title: "Spese di Gestione", icon: Receipt,
    body: "Carburante, assicurazioni, commercialista… Con «Nuova Spesa» puoi fotografare lo scontrino: importo, fornitore e data si compilano da soli." },
  { target: "sidebar-nav-agenda", title: "Agenda & Scadenze", icon: Calendar,
    body: "Vista Mese, Settimana e Giorno come Google Calendar: trascina un appuntamento per spostarlo, tira il bordo per cambiare l'orario, scegli un colore per ogni appuntamento o collaboratore. Clicca su un orario libero per crearne uno nuovo." },
  { target: "sidebar-nav-agent", title: "Assistente IA", icon: Sparkles,
    body: "Scrivi cosa ti serve in parole semplici: prepara preventivi, fissa appuntamenti, aggiunge clienti e segna i pagamenti. Prima di fare qualcosa ti chiede sempre conferma. Si attiva e disattiva quando vuoi." },
  { target: "sidebar-nav-settings", title: "Impostazioni Brand", icon: Sliders,
    body: "Carica il tuo logo e inserisci indirizzo, IBAN e colore aziendale: compariranno su preventivi, fatture e report." },
  { target: "notifications-bell", title: "Notifiche", icon: Bell,
    body: "La campanella ti avvisa di scadenze e appuntamenti. Attiva anche le notifiche push per riceverle sul telefono." },
  { target: "start-tour-button", title: "Rivedi la guida quando vuoi", icon: HelpCircle,
    body: "Hai finito! Se ti serve, riapri questa guida da qui. Buon lavoro!" },
];

const visibleRect = (testid) => {
  const el = testid && document.querySelector(`[data-testid="${testid}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 ? r : null;
};

const cardPos = (r) => {
  const W = Math.min(360, window.innerWidth - 32);
  if (!r) return { left: (window.innerWidth - W) / 2, top: Math.max(24, window.innerHeight / 2 - 150), width: W };
  if (r.left > window.innerWidth / 2) return { left: Math.max(16, r.right - W), top: r.bottom + 14, width: W };
  return { left: Math.min(r.right + 16, window.innerWidth - W - 16), top: Math.min(Math.max(16, r.top - 24), window.innerHeight - 280), width: W };
};

const FULL_ONLY = new Set(["sidebar-nav-price-list", "sidebar-nav-preziario", "sidebar-nav-quotes", "sidebar-nav-invoices", "sidebar-nav-expenses"]);

export const OnboardingTour = ({ open, onClose, simple = false }) => {
  const steps = TOUR_STEPS.filter((s) => (!simple || !FULL_ONLY.has(s.target))
    && (!s.target || document.querySelector(`[data-testid="${s.target}"]`) || window.innerWidth < 1024));
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[Math.min(i, steps.length - 1)];

  const measure = useCallback(() => setRect(visibleRect(step?.target)), [step]);
  useLayoutEffect(() => {
    if (!open) return;
    document.querySelector(`[data-testid="${step?.target}"]`)?.scrollIntoView?.({ block: "nearest" });
    measure();
  }, [open, i, measure, step]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((x) => Math.min(x + 1, steps.length - 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(x - 1, 0));
    };
    window.addEventListener("resize", measure);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("keydown", onKey); };
  }, [open, measure, onClose, steps.length]);
  useEffect(() => { if (open) setI(0); }, [open]);

  if (!open || !step) return null;
  const last = i >= steps.length - 1;
  const pos = cardPos(rect);
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour">
      {rect ? (
        <div className="absolute rounded-lg pointer-events-none transition-all duration-300 ring-2 ring-primary"
          style={{ left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12, boxShadow: "0 0 0 9999px rgba(2,6,23,0.68)" }} />
      ) : <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />}
      <div className="absolute rounded-2xl bg-card text-card-foreground border border-border shadow-2xl p-5 transition-all duration-300 animate-in-up"
        style={pos} data-testid="tour-card" role="dialog" aria-label={step.title}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Passo {i + 1} di {steps.length}</div>
            <h3 className="font-heading font-bold text-lg leading-tight mt-0.5" data-testid="tour-step-title">{step.title}</h3>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed mt-3" data-testid="tour-step-body">{step.body}</p>
        <div className="flex gap-1 mt-4">
          {steps.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all duration-300 ${k === i ? "w-5 bg-primary" : "w-1.5 bg-border"}`} />)}
        </div>
        <div className="flex items-center gap-2 mt-4">
          {!last && <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground mr-auto" data-testid="tour-skip-button">Salta guida</button>}
          {last && <span className="mr-auto" />}
          {i > 0 && <Button variant="outline" size="sm" onClick={() => setI(i - 1)} data-testid="tour-prev-button">Indietro</Button>}
          <Button size="sm" onClick={() => (last ? onClose() : setI(i + 1))} data-testid="tour-next-button">{last ? "Fine" : "Avanti"}</Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
